import {
  Alert,
  Box,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { CustomButton } from 'src/components/button/Button';
import { SimpleDialog } from 'src/components/modal';
import { Pagination } from 'src/components/pagination/Pagination';
import { Progress } from 'src/components/progress/Progress';
import { queryClient } from 'src/components/queryProvider';
import { SubHeaderWithPage } from 'src/components/subHeader/SubHeaderWithPage';
import {
  StyledTableCellHead,
  TableBodyContainer,
} from 'src/components/table/CustomTable.styled';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { IAssembly } from 'src/interfaces';
import { lang, queryKeys, theme, useUserRole } from 'src/utils';

import { columns } from './BulkReplaceReviewPage.helper';
import { REVIEW_PAGE_COLUMN } from './BulkReplaceReviewPage.interface';
import {
  Button,
  CustomCheckbox,
  CustomTableCell,
  FooterButtonsContainer,
  Wrapper,
} from './BulkReplaceReviewPage.styled';
import { useBulkReplaceReviewPage } from './hooks/BulkReplaceReviewPage.hook';
import { BulkReplaceReviewTableRow } from './modules/bulkReplaceReviewTableRow/BulkReplaceReviewTableRow';
import { BulkReplaceSummaryPage } from './modules/bulkReplaceSummaryPage/BulkReplaceSummaryPage';

export const BulkReplaceReviewPage = () => {
  const navigate = useNavigate();
  const { isViewOnly } = useUserRole();

  const {
    expendAssemblies,
    saveModalOpen,
    discardModalOpen,
    displaySummaryPage,
    updatedAssemblyComponents,
    setSaveModalOpen,
    setDiscardModalOpen,
    onExpendedAssemblies,
    onSelectedAssemblyIds,
    isLoadingAssemblyComponents,
    selectedAssemblyIds,
    allowedAssemblyIds,
    isLoadingBulkReplaceEligibleAssemblyIdData,
    assembliesList,
    selectedComponent,
    assembliesListMapped,
    bulkReplaceAssemblies,
    isLoadingAssembly,
    onCloseSummaryPage,
    defaultAssemblies,
    setStageBulkReplaceNameModal,
    stageBulkReplaceNameModal,
    setStageBulkDisplayName,
    stageBulkDisplayName,
    onHandleSaveBulkReplaceStage,
    isLoading: isStageBulkReplaceLoading,
    stageAssemblyData,
    setSelectedAssemblyIds,
  } = useBulkReplaceReviewPage();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    // invalidating the cache when the component is unmounted
    return () => {
      queryClient.invalidateQueries(
        queryKeys.bulkReplaceEligibleAssemblyIds.invalidateAll(),
      );
    };
  }, []);

  const handleCancel = () => {
    setStageBulkReplaceNameModal(false);
    setStageBulkDisplayName('');
  };

  const handleChangePage = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setRowsPerPage(newItemsPerPage);
    setPage(1);
  };

  const getAllSelectableAssemblyIds = () => {
    const selectableIds: number[] = [];
    assembliesList?.forEach((assembly) => {
      if (
        allowedAssemblyIds?.includes(assembly.id) &&
        !stageAssemblyData.some(
          (item: { assembly_id: number }) => item.assembly_id === assembly.id,
        )
      ) {
        selectableIds.push(assembly.id);
      }
      assembly.childrenAssemblies?.forEach((child: IAssembly) => {
        if (
          allowedAssemblyIds?.includes(child.id) &&
          !stageAssemblyData.some(
            (item: { assembly_id: number }) => item.assembly_id === child.id,
          )
        ) {
          selectableIds.push(child.id);
        }
      });
    });

    return selectableIds;
  };

  const selectableAssemblyIds = getAllSelectableAssemblyIds();
  const isAllSelected =
    selectableAssemblyIds.length > 0 &&
    selectableAssemblyIds.every((id) => selectedAssemblyIds.includes(id));

  const handleSelectAllClick = () => {
    // Only select all, no toggle behavior
    const newSelectedIds = [
      ...selectedAssemblyIds,
      ...selectableAssemblyIds.filter(
        (id) => !selectedAssemblyIds.includes(id),
      ),
    ];
    setSelectedAssemblyIds(newSelectedIds);
  };

  const selectedEnabledCount = selectedAssemblyIds.filter((id) =>
    selectableAssemblyIds.includes(id),
  ).length;

  const getTotalCountWithChildren = () => {
    let count = 0;
    assembliesList?.forEach((assembly) => {
      count++; // Count parent assembly
      count += assembly.childrenAssemblies?.length || 0; // Count child assemblies
    });

    return count;
  };

  const totalCountWithChildren = getTotalCountWithChildren();
  const parentAssembliesCount = assembliesList?.length || 0;
  const totalPages = Math.ceil(parentAssembliesCount / rowsPerPage);
  const totalCount = totalCountWithChildren;

  const paginatedAssemblies =
    assembliesList?.slice(
      (page - 1) * rowsPerPage,
      (page - 1) * rowsPerPage + rowsPerPage,
    ) || [];

  // Calculate start and end based on actual items shown on current page
  const getCountBeforePage = () => {
    let count = 0;
    const itemsBeforePage =
      assembliesList?.slice(0, (page - 1) * rowsPerPage) || [];
    itemsBeforePage.forEach((assembly) => {
      count++; // Count parent assembly
      count += assembly.childrenAssemblies?.length || 0; // Count child assemblies
    });

    return count;
  };

  const getCountOnCurrentPage = () => {
    let count = 0;
    paginatedAssemblies.forEach((assembly) => {
      count++; // Count parent assembly
      count += assembly.childrenAssemblies?.length || 0; // Count child assemblies
    });

    return count;
  };

  const countBeforePage = getCountBeforePage();
  const countOnCurrentPage = getCountOnCurrentPage();
  const start = totalCount > 0 ? countBeforePage + 1 : 0;
  const end = countBeforePage + countOnCurrentPage;

  const getPageSelectableAssemblyIds = () => {
    const selectableIds: number[] = [];
    paginatedAssemblies?.forEach((assembly) => {
      if (
        allowedAssemblyIds?.includes(assembly.id) &&
        !stageAssemblyData.some(
          (item: { assembly_id: number }) => item.assembly_id === assembly.id,
        )
      ) {
        selectableIds.push(assembly.id);
      }
      assembly.childrenAssemblies?.forEach((child: IAssembly) => {
        if (
          allowedAssemblyIds?.includes(child.id) &&
          !stageAssemblyData.some(
            (item: { assembly_id: number }) => item.assembly_id === child.id,
          )
        ) {
          selectableIds.push(child.id);
        }
      });
    });

    return selectableIds;
  };

  const pageSelectableIds = getPageSelectableAssemblyIds();
  const isPageAllSelected =
    pageSelectableIds.length > 0 &&
    pageSelectableIds.every((id) => selectedAssemblyIds.includes(id));

  const handlePageSelectAllClick = () => {
    if (isPageAllSelected) {
      // Deselect all selectable IDs on current page
      const remainingSelected = selectedAssemblyIds.filter(
        (id) => !pageSelectableIds.includes(id),
      );
      setSelectedAssemblyIds(remainingSelected);
    } else {
      // Select all selectable IDs on current page
      const newSelectedIds = [
        ...selectedAssemblyIds,
        ...pageSelectableIds.filter((id) => !selectedAssemblyIds.includes(id)),
      ];
      setSelectedAssemblyIds(newSelectedIds);
    }
  };

  if (displaySummaryPage) {
    return (
      <>
        <BulkReplaceSummaryPage
          handleCloseSummaryPage={onCloseSummaryPage}
          assemblyData={assembliesListMapped}
          updatedAssemblyComponents={updatedAssemblyComponents}
          stageBulkDisplayName={stageBulkDisplayName}
        />
      </>
    );
  }

  return isLoadingBulkReplaceEligibleAssemblyIdData ||
    isLoadingAssembly ||
    isLoadingAssemblyComponents ? (
    <Progress />
  ) : (
    <Wrapper>
      {isViewOnly && <ViewOnlyAlert />}
      <Breadcrumbs
        items={[
          {
            label: lang.assemblies,
            link: PATHS.ASSEMBLIES,
          },
          {
            label: lang.bulkReplace,
          },
        ]}
      />
      <Box>
        <SubHeaderWithPage
          title={lang.reviewChanges}
          currentPage={page}
          totalPages={totalPages}
          itemsPerPage={rowsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          isSelectedAll={false}
          onSelectAllClick={handleSelectAllClick}
          disableSelectAll={isAllSelected}
          startItem={start}
          endItem={end}
          totalCount={totalCount}
        />
      </Box>

      {selectedEnabledCount > 0 && (
        <Alert
          severity="info"
          icon={false}
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'center',
            '& .MuiAlert-message': {
              width: '100%',
              textAlign: 'center',
            },
          }}>
          {`${selectedEnabledCount} ${
            selectedEnabledCount === 1 ? 'item' : 'items'
          } selected `}
          <span
            onClick={() => setSelectedAssemblyIds([])}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              marginLeft: '4px',
            }}>
            clear selection
          </span>
        </Alert>
      )}

      <TableBodyContainer component={Paper}>
        <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <StyledTableCellHead
                  key={column.key}
                  padding={
                    column.key === REVIEW_PAGE_COLUMN.CHECK_BOX
                      ? 'checkbox'
                      : 'normal'
                  }
                  sx={
                    column.key === REVIEW_PAGE_COLUMN.CHECK_BOX
                      ? {}
                      : { width: column.width }
                  }>
                  {column.key === REVIEW_PAGE_COLUMN.CHECK_BOX ? (
                    <CustomCheckbox
                      color="primary"
                      checked={isPageAllSelected}
                      indeterminate={
                        !isPageAllSelected &&
                        pageSelectableIds.some((id) =>
                          selectedAssemblyIds.includes(id),
                        )
                      }
                      disabled={pageSelectableIds.length === 0}
                      onChange={handlePageSelectAllClick}
                    />
                  ) : (
                    column.label
                  )}
                </StyledTableCellHead>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAssemblies?.map((assembly, key) => {
              const { childrenAssemblies, id } = assembly;

              return (
                <Fragment key={key}>
                  <BulkReplaceReviewTableRow
                    key={key}
                    onSelectedAssemblies={onSelectedAssemblyIds}
                    selectedAssemblies={selectedAssemblyIds}
                    expendAssemblies={expendAssemblies}
                    onExpendedAssemblies={onExpendedAssemblies}
                    assembly={assembly}
                    defaultAssemblies={defaultAssemblies}
                    selectedComponent={selectedComponent && selectedComponent}
                    allowedAssemblyIds={allowedAssemblyIds}
                    stagedAssembly={stageAssemblyData.find(
                      (item: { assembly_id: number }) =>
                        item.assembly_id === id,
                    )}
                    isInherited={
                      allowedAssemblyIds?.includes(assembly.id) &&
                      !assembly.assemblyComponents
                        ?.map(
                          (item) =>
                            selectedComponent &&
                            selectedComponent &&
                            item.component_id,
                        )
                        .includes(selectedComponent && selectedComponent?.id)
                    }
                    isChildRow={assembly.variation_parent_id ? true : false}
                    isParentChecked={
                      (assembly.variation_parent_id &&
                        allowedAssemblyIds?.includes(
                          assembly.variation_parent_id,
                        ) &&
                        selectedAssemblyIds.includes(
                          assembly.variation_parent_id,
                        )) ||
                      null
                    }
                  />
                  {childrenAssemblies.length ? (
                    <TableRow>
                      <CustomTableCell colSpan={6} isChildRow>
                        <Collapse
                          in={expendAssemblies.includes(id)}
                          timeout="auto"
                          unmountOnExit>
                          {childrenAssemblies.map(
                            (childAssembly: IAssembly) => (
                              <TableContainer>
                                <Table>
                                  <TableBody>
                                    <BulkReplaceReviewTableRow
                                      onSelectedAssemblies={
                                        onSelectedAssemblyIds
                                      }
                                      selectedAssemblies={selectedAssemblyIds}
                                      defaultAssemblies={defaultAssemblies}
                                      expendAssemblies={expendAssemblies}
                                      onExpendedAssemblies={
                                        onExpendedAssemblies
                                      }
                                      assembly={childAssembly}
                                      allowedAssemblyIds={allowedAssemblyIds}
                                      stagedAssembly={stageAssemblyData.find(
                                        (item: { assembly_id: number }) =>
                                          item.assembly_id === childAssembly.id,
                                      )}
                                      isInherited={
                                        allowedAssemblyIds?.includes(
                                          childAssembly.id,
                                        ) &&
                                        !childAssembly.assemblyComponents
                                          ?.map(
                                            (item) =>
                                              selectedComponent &&
                                              selectedComponent &&
                                              item.component_id,
                                          )
                                          .includes(
                                            selectedComponent &&
                                              selectedComponent?.id,
                                          )
                                      }
                                      isChildRow={
                                        childAssembly.variation_parent_id
                                          ? true
                                          : false
                                      }
                                      isParentChecked={
                                        (childAssembly.variation_parent_id &&
                                          allowedAssemblyIds?.includes(
                                            childAssembly.variation_parent_id,
                                          ) &&
                                          selectedAssemblyIds.includes(
                                            childAssembly.variation_parent_id,
                                          )) ||
                                        null
                                      }
                                    />
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ),
                          )}
                        </Collapse>
                      </CustomTableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableBodyContainer>

      <Pagination
        page={page}
        totalPages={totalPages}
        onChange={handleChangePage}
        loading={
          isLoadingBulkReplaceEligibleAssemblyIdData ||
          isLoadingAssembly ||
          isLoadingAssemblyComponents
        }
      />

      <SimpleDialog
        open={saveModalOpen}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        modalSize="sm"
        title={lang.bulkStageReviewModal.title}
        subTitle={lang.bulkStageReviewModal.subTitle}
        onClose={() => {
          setSaveModalOpen(false);
          setDiscardModalOpen(true);
        }}
        onClickCancel={() => {
          setSaveModalOpen(false);
          setDiscardModalOpen(true);
        }}
        onClickConfirm={() => {
          setSaveModalOpen(false);
          setStageBulkReplaceNameModal(true);
        }}></SimpleDialog>

      <SimpleDialog
        open={discardModalOpen}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={() => setDiscardModalOpen(false)}
        onClickCancel={() => setDiscardModalOpen(false)}
        onClickConfirm={() => {
          setDiscardModalOpen(false);
          navigate({
            pathname: PATHS.ASSEMBLIES,
          });
        }}
      />

      <SimpleDialog
        open={stageBulkReplaceNameModal}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.bulkStageConfirmModal.title}
        inputValue={stageBulkDisplayName}
        setInputValue={(e) => {
          if (e.target.value.length <= 100) {
            setStageBulkDisplayName(e.target.value);
          }
        }}
        isInputRequired={true}
        disabled={!stageBulkDisplayName?.trim()}
        onClose={() => setStageBulkReplaceNameModal(false)}
        onClickCancel={handleCancel}
        onClickConfirm={() => {
          onHandleSaveBulkReplaceStage();
        }}
        isLoading={isStageBulkReplaceLoading}
      />

      {!isViewOnly && (
        <FooterButtonsContainer>
          <CustomButton
            variant="text"
            onClick={() => setDiscardModalOpen(true)}>
            {lang.cancel}
          </CustomButton>
          <Button
            variant="contained"
            disabled={bulkReplaceAssemblies?.length === 0}
            onClick={() => setSaveModalOpen(true)}>
            {lang.save}
          </Button>
        </FooterButtonsContainer>
      )}
    </Wrapper>
  );
};
